import { MiraAvailabilityState, MiraPublishedMetric, MiraPublishedPeriod } from "@/data/models/Mira";
import {
  COLOR_MIRA_OUTCOME_1,
  COLOR_MIRA_OUTCOME_1_DARK,
  COLOR_MIRA_OUTCOME_2,
  COLOR_MIRA_OUTCOME_2_DARK,
  COLOR_MIRA_OUTCOME_3,
  COLOR_MIRA_OUTCOME_3_DARK,
  COLOR_MIRA_STAGE_ASSESSMENT,
  COLOR_MIRA_STAGE_ASSESSMENT_DARK,
  COLOR_MIRA_STAGE_DRYING,
  COLOR_MIRA_STAGE_DRYING_DARK,
  COLOR_MIRA_STAGE_END,
  COLOR_MIRA_STAGE_END_DARK,
  COLOR_MIRA_STAGE_SETUP,
  COLOR_MIRA_STAGE_SETUP_DARK,
} from "@/utils/colors";
import {
  MiraSegment,
  MiraTile,
  MiraViewDelta,
  formatPercent,
  metricTitle,
  publishedMetric,
} from "@/utils/MiraDataQualityView";
import { MiraDryingContext, MiraFact, durationFormatter, durationTileTexts, durationUnit, formatDays, formatDuration } from "@/utils/MiraDryingView";
import { MiraValueFormatter } from "@/utils/MiraTrendChart";
import { MiraStackedSegmentDef } from "@/utils/MiraStackedTrendView";

/**
 * The Prozess screen's view model: what each tile, bucket and part shows, built from a published period
 * and its comparison period. Pure, like the other Mira view models.
 *
 * Decided by Taimoor on 2026-10-01, pinned by a test, and narrowed on 2026-10-05 (Hans, #2610009):
 * - **Phasendauer, Wartezeit and Reihenfolge der Projektschritte are gone** (PHA-001 / PHA-002 /
 *   PHA-009): the phase and wait dot plots and the step-order tile and card were removed.
 * - As on the other screens: changes are neutral, the mean leads with the median beside it, and only a
 *   published figure gets a tile.
 */

/** The recording routes of Erfassungsquelle, in display order, each with its colour. */
const ROUTES = [
  { key: "viaApp", color: COLOR_MIRA_OUTCOME_1, dark: COLOR_MIRA_OUTCOME_1_DARK },
  { key: "viaErp", color: COLOR_MIRA_OUTCOME_2, dark: COLOR_MIRA_OUTCOME_2_DARK },
  { key: "viaPortal", color: COLOR_MIRA_OUTCOME_3, dark: COLOR_MIRA_OUTCOME_3_DARK },
];

function tile(
  id: string,
  icon: string,
  value: string,
  caption: string | null,
  delta: MiraViewDelta | null,
  ctx: MiraDryingContext,
  spread: string | null = null
): MiraTile {
  return { id, icon, title: metricTitle(id, ctx), value, caption, delta, status: null, statusLabel: "", spread };
}

/**
 * The Prozess transitions (Hans, Track #2610040, P4): one tile per transition between two Zeitleiste
 * steps, grouped along the project's path, in Hans's order. GZ's own approval-to-drying (PHA-022) sits
 * with the order steps, since a GZ project has no leak detection or damage assessment.
 */
export const PROCESS_TRANSITION_GROUPS: ReadonlyArray<{ key: string; tiles: ReadonlyArray<{ id: string; icon: string }> }> = [
  {
    key: "order",
    tiles: [
      { id: "MIRA-PHA-012", icon: "mdi-file-sign" },
      { id: "MIRA-PHA-013", icon: "mdi-clipboard-search-outline" },
      { id: "MIRA-PHA-014", icon: "mdi-magnify" },
      { id: "MIRA-PHA-022", icon: "mdi-tools" },
    ],
  },
  {
    key: "drying",
    tiles: [
      { id: "MIRA-PHA-015", icon: "mdi-fan" },
      { id: "MIRA-PHA-016", icon: "mdi-check-decagram" },
      { id: "MIRA-PHA-017", icon: "mdi-play-circle-outline" },
      { id: "MIRA-PHA-018", icon: "mdi-calendar-range" },
    ],
  },
  {
    key: "restoration",
    tiles: [
      { id: "MIRA-PHA-019", icon: "mdi-timer-sand" },
      // Wiederherstellungsdauer (PHA-020) is hidden until IRES Project records a real restoration start:
      // it stamps the start in the same minute as the conclusion, so it reads about 0 (Hans, 2026-10-14).
      { id: "MIRA-PHA-021", icon: "mdi-flag-checkered" },
    ],
  },
];

/**
 * The Prozess KPI row: the transitions (#2610040, P4), in the order of {@link PROCESS_TRANSITION_GROUPS}.
 * They replace the "Dauer bis ..." tiles (PHA-003 to PHA-007) and the Projektablauf card (PHA-011), which
 * IRES Cloud still computes until Hans has seen the transitions (Taimoor, 2026-10-13). Phasendauer,
 * Wartezeit, Reihenfolge der Projektschritte and the Stehende Projekte snapshot (PHA-008, retired
 * 2026-10-13) have no tile either.
 */
export function buildProcessTiles(
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  ctx: MiraDryingContext
): MiraTile[] {
  const tiles: MiraTile[] = [];

  const add = (id: string, icon: string, build: (metric: MiraPublishedMetric, previous: MiraPublishedMetric | null) => MiraTile) => {
    if (period?.metrics[id]?.state !== MiraAvailabilityState.AVAILABLE) return;
    const metric = publishedMetric(period, id);
    // No tile without a value (Taimoor, 2026-10-02): what a company would unlock goes on its own page (D2).
    if (metric) tiles.push(build(metric, publishedMetric(comparison, id)));
  };
  // The transition's name already says where it runs from and to, so the caption carries the median and
  // the projects with both steps.
  for (const group of PROCESS_TRANSITION_GROUPS) {
    for (const { id, icon } of group.tiles) {
      add(id, icon, (metric, previous) => {
        const texts = durationTileTexts(metric, previous, ctx);
        return tile(id, icon, texts.value,
          ctx.t("mira.processFlow.captions.transition", [texts.median, ctx.formatNumber(metric.basis ?? 0)]),
          texts.delta, ctx, texts.spread);
      });
    }
  }

  return tiles;
}

/**
 * The formatter the Verlauf card uses for a Prozess figure: every remaining Prozess figure is a
 * duration, in days, or in hours where the tile's headline for the chosen period is under one day.
 * Neutral, like every Prozess figure.
 */
export function processTrendFormatter(metricId: string, ctx: MiraDryingContext, period: MiraPublishedPeriod | null = null): MiraValueFormatter {
  return durationFormatter(durationUnit(publishedMetric(period, metricId)?.value), ctx);
}

/** PHA-010: the steps recorded through the app, the ERP and the portal, as parts of one bar. */
export function buildRouteSegments(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraSegment[] {
  const breakdown = publishedMetric(period, "MIRA-PHA-010")?.breakdown ?? {};
  const total = ROUTES.reduce((sum, route) => sum + (breakdown[route.key] ?? 0), 0);
  if (!total) return [];

  return ROUTES.filter((route) => (breakdown[route.key] ?? 0) > 0).map((route) => ({
    key: route.key,
    label: ctx.t(`mira.processFlow.routes.${route.key}`),
    value: breakdown[route.key] ?? 0,
    valueText: ctx.formatNumber(breakdown[route.key] ?? 0),
    shareText: formatPercent((breakdown[route.key] ?? 0) / total, ctx),
    color: ctx.dark ? route.dark : route.color,
  }));
}

/** The steps that recorded no route at all, beside the parts. */
export function routeFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = publishedMetric(period, "MIRA-PHA-010");
  if (!metric) return [];

  return [{ label: ctx.t("mira.processFlow.facts.noRoute"), value: ctx.formatNumber(metric.breakdown?.noRoute ?? 0) }];
}

// --- Projektablauf (MIRA-PHA-011): the four stages of a project stacked over time (2610009) ---

/**
 * The four project stages, in stack order, in the hues the project list badges give those states
 * (Schadenaufnahme brown, Trocknungsaufbau and Trocknung the two blues, Projektende grey). Each part's
 * value is a mean number of days, and the four sum to the whole project's mean duration. A segment key
 * matches the `segments` keys IRES Cloud writes on the MIRA-PHA-011 trend points.
 */
export function processStackSegments(ctx: MiraDryingContext): MiraStackedSegmentDef[] {
  return [
    { key: "toDamageAssessment", label: ctx.t("mira.processFlow.stack.stages.toDamageAssessment"), color: ctx.dark ? COLOR_MIRA_STAGE_ASSESSMENT_DARK : COLOR_MIRA_STAGE_ASSESSMENT },
    { key: "toDryingSetup", label: ctx.t("mira.processFlow.stack.stages.toDryingSetup"), color: ctx.dark ? COLOR_MIRA_STAGE_SETUP_DARK : COLOR_MIRA_STAGE_SETUP },
    { key: "drying", label: ctx.t("mira.processFlow.stack.stages.drying"), color: ctx.dark ? COLOR_MIRA_STAGE_DRYING_DARK : COLOR_MIRA_STAGE_DRYING },
    { key: "toProjectEnd", label: ctx.t("mira.processFlow.stack.stages.toProjectEnd"), color: ctx.dark ? COLOR_MIRA_STAGE_END_DARK : COLOR_MIRA_STAGE_END },
  ];
}

/** Whether the Projektablauf card is drawn: MIRA-PHA-011 is AVAILABLE for the chosen period. */
export function processStackAvailable(period: MiraPublishedPeriod | null): boolean {
  return period?.metrics["MIRA-PHA-011"]?.state === MiraAvailabilityState.AVAILABLE;
}

/** The stacked bar's values are mean days, so the total reads in days, like the duration tiles. */
export function processStackFormatter(ctx: MiraDryingContext): (value: number) => string {
  return (value) => formatDays(value, ctx);
}

/**
 * The cohort line under the Projektablauf card: how many finished projects had all four steps in order,
 * and how many were left out because their steps were out of order. `null` where the figure is absent.
 */
export function processStackCaption(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): string | null {
  const metric = publishedMetric(period, "MIRA-PHA-011");
  if (!metric) return null;
  return ctx.t("mira.processFlow.stack.caption", [
    ctx.formatNumber(metric.basis ?? 0),
    ctx.formatNumber(metric.breakdown?.leftOutOfOrder ?? 0),
  ]);
}

/**
 * The insurance split under the Projektablauf card, published only when at least 30 projects of the
 * cohort are insurance cases (so the two means do not rest on a handful): the time from the damage
 * assessment to the insurer's approval, and from the approval to the drying set-up. Empty otherwise.
 */
export function processStackFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = publishedMetric(period, "MIRA-PHA-011");
  const breakdown = metric?.breakdown;
  if (!breakdown || breakdown.damageToApproval === undefined || breakdown.approvalToSetup === undefined) {
    return [];
  }
  return [
    { label: ctx.t("mira.processFlow.stack.insuranceHeader", [ctx.formatNumber(breakdown.insuranceCases ?? 0)]), value: "" },
    { label: ctx.t("mira.processFlow.stack.damageToApproval"), value: formatDuration(breakdown.damageToApproval, ctx) },
    { label: ctx.t("mira.processFlow.stack.approvalToSetup"), value: formatDuration(breakdown.approvalToSetup, ctx) },
  ];
}
